import { useMemo, useState, useCallback, useEffect } from "react";
import styled from "@emotion/styled";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import Layout from "../../components/layout/Layout";
import Loading from "../../components/Loading";
import ErrorMessage from "../../components/Error";
import {
  useGetDisputesQuery,
  useUpdateAnswerMutation,
  answersApi,
} from "../../api/answers";
import { useCurrentUserQuestionnaireState } from "../../hooks/useCurrentUserQuestionnaireState";
import { formatErrorString } from "../../utils/format";
import { PrimaryButton } from "../../components/buttons/PrimaryButton";

import NegotiationHeader from "./components/NegotiationHeader";
import { DisputeCard } from "./components/DisputeCard";
import { ResolutionsModal } from "./components/ResolutionsModal";
import useCurrentUserPaymentData from "../../hooks/useCurrentUserPaymentData";
import RepresentationBlocker from "../../components/RepresentationBlocker";
import { FLEX_COLUMN, MOBILE_SCREEN } from "../../components/theme";
import { usePartnerQuestionnaireState } from "../../hooks/usePartnerQuestionnaireState";
import { useNotarizationState } from "../../hooks/useNotarizationState";
import NotarizationAccessBlocker from "../../components/NotarizationAccessBlocker";
import { useNegotiationState } from "../../hooks/useNegotiationState";
import { useGetIncomesByUserIdQuery } from "../../api/financials/incomes";
import { hasAllRequiredIncomeYears } from "../../utils/incomeValidation";

const DisputesContainer = styled.div`
  ${FLEX_COLUMN}
  gap: 40px;
`;

const StyledContinueButtonContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: 24px;
  ${MOBILE_SCREEN} {
    margin-top: 16px;
    width: 100%;
  }
`;

const StyledContinueButton = styled(PrimaryButton)`
  width: 326px;
  ${MOBILE_SCREEN} {
    width: 100%;
  }
`;

const Negotiations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [localError, setLocalError] = useState<string | null>(null);
  const [isResolutionsModalOpen, setIsResolutionsModalOpen] = useState(false);
  const [isAnyDisputeModalOpen, setIsAnyDisputeModalOpen] = useState(false);
  const {
    user,
    document,
    currentUserWillHireAttorney,
    representationPayment,
    isLoading: isUserLoading,
    error: userError,
  } = useCurrentUserPaymentData();
  const {
    data: disputes,
    isLoading: isDisputesLoading,
    error: disputesError,
  } = useGetDisputesQuery(user ? { userId: user.id } : skipToken);
  const {
    isNotarizationComplete,
    isLoading: isNotarizationLoading,
    error: isNotarizationError,
  } = useNotarizationState();

  const [updateAnswer, { isLoading: isUpdatingAnswer, error: updateError }] =
    useUpdateAnswerMutation();

  const { partnerName } = usePartnerQuestionnaireState();

  // Skip questionnaire query when dispute modals are open to avoid interference with change answer flow
  const shouldSkipQuestionnaireQuery =
    isUpdatingAnswer || isAnyDisputeModalOpen;

  const {
    isCurrentUserQuestionnaireComplete,
    firstIncompleteSection,
    isLoading: isQuestionnaireSectionsLoading,
    error: questionnaireSectionsError,
  } = useCurrentUserQuestionnaireState(user?.id, shouldSkipQuestionnaireQuery);

  // Check if partner has joined the platform
  const areAccountsLinked = !!document?.userBId;

  // Check current user's financial disclosure status
  const { data: currentUserIncomes } = useGetIncomesByUserIdQuery(
    user?.id && document?.id
      ? { userId: user.id, docId: document.id }
      : skipToken,
  );
  const currentUserHasFinancials = currentUserIncomes
    ? hasAllRequiredIncomeYears(currentUserIncomes)
    : false;

  const {
    isComplete: isNegotiationComplete,
    isLoading: isNegotiationLoading,
    isError: isNegotiationError,
    partnerHasFinancials,
    isPartnerQuestionnaireComplete,
  } = useNegotiationState({
    isQuestionnaireComplete: isCurrentUserQuestionnaireComplete,
    hasFinancials: currentUserHasFinancials,
    areAccountsLinked,
  });

  // Redirect to dashboard if partner hasn't joined yet
  useEffect(() => {
    if (document && !areAccountsLinked) {
      void navigate("/");
    }
  }, [document, areAccountsLinked, navigate]);

  // Manually invalidate disputes cache instead of on every updateAnswer mutation
  // Prevents change answers modal from closing automatically when changes are made
  const handleAnswerSaved = useCallback(() => {
    dispatch(answersApi.util.invalidateTags(["disputes"]));
  }, [dispatch]);

  // Force fresh fetch of disputes when component mounts
  useEffect(() => {
    if (user?.id) {
      dispatch(answersApi.util.invalidateTags(["disputes"]));
    }
  }, [dispatch, user?.id]);

  const handleAgreeWithPartner = async (questionId: string) => {
    setLocalError(null);

    if (!user?.id) {
      setLocalError("User session not found. Please refresh the page.");
      return;
    }

    if (!disputes) {
      setLocalError("Unable to load disputes. Please refresh the page.");
      return;
    }

    const dispute = disputes.find((d) => d.question.id === questionId);
    if (!dispute) {
      setLocalError("Dispute not found. Please refresh the page.");
      return;
    }

    const partnerAnswer =
      dispute.userId === user.id ? dispute.answerB : dispute.answerA;

    const updatePayload = {
      questionId,
      userId: user.id,
      ...(partnerAnswer.valueTyped && { valueTyped: partnerAnswer.valueTyped }),
      questionOptionIds: partnerAnswer.answerQuestionOptions.map(
        (aqo) => aqo.questionOptionId,
      ),
    };

    try {
      await updateAnswer(updatePayload).unwrap();
    } catch (error) {
      console.error("Failed to update answer:", error);
    }
  };

  // Sort disputes by question group order, then by question order within each group
  const sortedDisputes = useMemo(() => {
    if (!Array.isArray(disputes) || disputes.length === 0) {
      return [];
    }

    return [...disputes].sort((disputeA, disputeB) => {
      const groupOrderDiff =
        disputeA.question.parentQuestionGroup.order -
        disputeB.question.parentQuestionGroup.order;

      if (groupOrderDiff !== 0) return groupOrderDiff;

      return disputeA.question.order - disputeB.question.order;
    });
  }, [disputes]);

  const hasDisputes = sortedDisputes.length > 0;

  const shouldShowDisputes =
    hasDisputes &&
    isPartnerQuestionnaireComplete &&
    isCurrentUserQuestionnaireComplete &&
    currentUserHasFinancials &&
    partnerHasFinancials;
  const handleContinue = async () => {
    await navigate("/documents");
  };

  const handleNavigateToIncompleteSection = useCallback(() => {
    if (firstIncompleteSection) {
      void navigate(firstIncompleteSection.path);
    }
  }, [firstIncompleteSection, navigate]);

  if (
    userError ||
    disputesError ||
    isNotarizationError ||
    questionnaireSectionsError ||
    isNegotiationError
  ) {
    return (
      <Layout>
        <ErrorMessage>
          Could not load alignments. Refresh the page and if the problem
          persists use the chat bubble to reach out to our support team.
        </ErrorMessage>
      </Layout>
    );
  }

  if (
    isUserLoading ||
    isDisputesLoading ||
    isNotarizationLoading ||
    isQuestionnaireSectionsLoading ||
    isNegotiationLoading
  ) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  if (currentUserWillHireAttorney && !representationPayment) {
    return (
      <Layout>
        <RepresentationBlocker />
      </Layout>
    );
  }

  if (isNotarizationComplete) {
    return (
      <Layout>
        <NotarizationAccessBlocker />
      </Layout>
    );
  }

  return (
    <Layout>
      <NegotiationHeader
        hasDisputes={hasDisputes}
        isPartnerQuestionnaireComplete={isPartnerQuestionnaireComplete}
        isCurrentUserQuestionnaireComplete={isCurrentUserQuestionnaireComplete}
        partnerName={partnerName}
        user={user}
        areAccountsLinked={areAccountsLinked}
        onViewHistory={() => {
          setIsResolutionsModalOpen(true);
        }}
        showHistoryLink={areAccountsLinked}
        onNavigateToIncompleteSection={handleNavigateToIncompleteSection}
        currentUserHasFinancials={currentUserHasFinancials}
        partnerHasFinancials={partnerHasFinancials}
      />
      {(localError ?? updateError) && (
        <ErrorMessage>
          {localError ?? (updateError ? formatErrorString(updateError) : null)}
        </ErrorMessage>
      )}
      {shouldShowDisputes && (
        <DisputesContainer>
          {sortedDisputes.map((dispute) => (
            <DisputeCard
              key={dispute.question.id}
              dispute={dispute}
              currentUserId={user?.id ?? ""}
              onAgreeWithPartner={handleAgreeWithPartner}
              onAnswerSaved={handleAnswerSaved}
              isUpdatingAnswer={isUpdatingAnswer}
              onModalStateChange={setIsAnyDisputeModalOpen}
            />
          ))}
        </DisputesContainer>
      )}
      {!hasDisputes &&
        isCurrentUserQuestionnaireComplete &&
        isNegotiationComplete && (
          <StyledContinueButtonContainer>
            <StyledContinueButton onClick={handleContinue}>
              Continue
            </StyledContinueButton>
          </StyledContinueButtonContainer>
        )}
      {user && document && isResolutionsModalOpen && (
        <ResolutionsModal
          onClose={() => {
            setIsResolutionsModalOpen(false);
          }}
          onResolutionChanged={handleAnswerSaved}
          userId={user.id}
          documentId={document.id}
        />
      )}
    </Layout>
  );
};

export default Negotiations;
